/**
 * Shared i18n catalogs (ISSUE-125 / T7.1 + T7.2).
 *
 * Single source of truth for translation catalogs consumed by every surface:
 * web/mobile/landing render them through react-i18next; the API renders
 * localized emails/push from the SAME resources via a standalone i18next
 * instance (apps/api/src/i18n.ts). Each surface owns its own i18next init —
 * this module only ships catalogs + locale metadata so the package stays free
 * of framework runtime.
 *
 * Catalogs live as JSON under ./<locale>/<namespace>.json. `tsc` does not copy
 * JSON to dist, so the shared build runs a copy step (see package.json build)
 * — production consumers (web/mobile/landing prod bundles) read these from dist.
 *
 * T7.2 extends Phase b with: log, coach, paywall, progress, landing, push
 * namespaces; full pt-PT translation across all 8 namespaces.
 */
import { ALERT_BANNED_WORDS } from '../schemas/notifications.js';
import enCoach from './en/coach.json' with { type: 'json' };
import enCommon from './en/common.json' with { type: 'json' };
import enCycle from './en/cycle.json' with { type: 'json' };
import enEmail from './en/email.json' with { type: 'json' };
import enInsights from './en/insights.json' with { type: 'json' };
import enLanding from './en/landing.json' with { type: 'json' };
import enLog from './en/log.json' with { type: 'json' };
import enMeasurements from './en/measurements.json' with { type: 'json' };
import enPaywall from './en/paywall.json' with { type: 'json' };
import enPlanning from './en/planning.json' with { type: 'json' };
import enProgress from './en/progress.json' with { type: 'json' };
import enPush from './en/push.json' with { type: 'json' };
import enSettings from './en/settings.json' with { type: 'json' };
import enSharing from './en/sharing.json' with { type: 'json' };
import enSummary from './en/summary.json' with { type: 'json' };
import enToday from './en/today.json' with { type: 'json' };
import ptCoach from './pt-PT/coach.json' with { type: 'json' };
import ptCommon from './pt-PT/common.json' with { type: 'json' };
import ptCycle from './pt-PT/cycle.json' with { type: 'json' };
import ptEmail from './pt-PT/email.json' with { type: 'json' };
import ptInsights from './pt-PT/insights.json' with { type: 'json' };
import ptLanding from './pt-PT/landing.json' with { type: 'json' };
import ptLog from './pt-PT/log.json' with { type: 'json' };
import ptMeasurements from './pt-PT/measurements.json' with { type: 'json' };
import ptPaywall from './pt-PT/paywall.json' with { type: 'json' };
import ptPlanning from './pt-PT/planning.json' with { type: 'json' };
import ptProgress from './pt-PT/progress.json' with { type: 'json' };
import ptPush from './pt-PT/push.json' with { type: 'json' };
import ptSettings from './pt-PT/settings.json' with { type: 'json' };
import ptSharing from './pt-PT/sharing.json' with { type: 'json' };
import ptSummary from './pt-PT/summary.json' with { type: 'json' };
import ptToday from './pt-PT/today.json' with { type: 'json' };
/** Supported UI locales. `en` is the source; `pt-PT` is the first translation. */
export const LOCALES = ['en', 'pt-PT'];
export const DEFAULT_LOCALE = 'en';
/** Fallback chain end (ISSUE-125 §2.3): pt-PT → en. */
export const FALLBACK_LOCALE = 'en';
/** Catalog namespaces (Phase b + T7.2 full sweep + T16.3 planning + T16.4 measurements
 *  + T28.2 summary/recap + T29.2 today). */
export const NAMESPACES = [
    'common',
    'settings',
    'email',
    'log',
    'coach',
    'paywall',
    'planning',
    'measurements',
    'progress',
    'landing',
    'push',
    'cycle',
    'sharing',
    'summary',
    'today',
    'insights',
];
/** i18next `resources` map: { locale: { namespace: catalog } }. */
export const resources = {
    en: {
        common: enCommon,
        settings: enSettings,
        email: enEmail,
        log: enLog,
        coach: enCoach,
        paywall: enPaywall,
        planning: enPlanning,
        measurements: enMeasurements,
        progress: enProgress,
        landing: enLanding,
        push: enPush,
        cycle: enCycle,
        sharing: enSharing,
        summary: enSummary,
        today: enToday,
        insights: enInsights,
    },
    'pt-PT': {
        common: ptCommon,
        settings: ptSettings,
        email: ptEmail,
        log: ptLog,
        coach: ptCoach,
        paywall: ptPaywall,
        planning: ptPlanning,
        measurements: ptMeasurements,
        progress: ptProgress,
        landing: ptLanding,
        push: ptPush,
        cycle: ptCycle,
        sharing: ptSharing,
        summary: ptSummary,
        today: ptToday,
        insights: ptInsights,
    },
};
export function isSupportedLocale(value) {
    return LOCALES.includes(value);
}
/**
 * Narrow any incoming locale tag to a supported `Locale` (ISSUE-125 §2.2/§2.3).
 * Region-less `pt` and any `pt-*` (incl. pt-BR) resolve to `pt-PT` — we never
 * fall through to pt-BR defaults. Unknown tags fall back to `en`.
 */
export function resolveLocale(input) {
    if (!input)
        return DEFAULT_LOCALE;
    const lower = input.toLowerCase();
    if (lower === 'pt' || lower.startsWith('pt-') || lower.startsWith('pt_'))
        return 'pt-PT';
    if (lower === 'en' || lower.startsWith('en-') || lower.startsWith('en_'))
        return 'en';
    return DEFAULT_LOCALE;
}
/** Human-readable language name for the AI reply-language clause (§3). */
export function localeName(locale) {
    switch (locale) {
        case 'pt-PT':
            return 'Portuguese (Portugal)';
        default:
            return 'English';
    }
}
export { isSensitiveKey, SENSITIVE_KEYS } from './sensitive-keys.js';
/**
 * Per-locale ED-safe banned lexicon (ISSUE-125 §5). The `en` entry reuses the
 * canonical ADR-0009 alert list so existing alert/gating tests stay green; each
 * new locale supplies its own list. Sensitive/user-facing catalog values must
 * not contain these words — enforced by the per-locale lexicon test.
 */
export const BANNED_LEXICON = {
    en: ALERT_BANNED_WORDS,
    // pt-PT seed: brand-guide "don't" lexicon translated (restriction / guilt /
    // shame framing). Finalized with the full translation in T7.2.
    'pt-PT': [
        'mau',
        'batota',
        'estragado',
        'estragaste',
        'falhaste',
        'falhado',
        'atrasado',
        'culpa',
        'preguiçoso',
        'fraco',
        'terrível',
        'horrível',
        'nojento',
    ],
};
//# sourceMappingURL=index.js.map